import { Component, OnInit, Inject } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AccountsService, PersonsService } from '@app/core/stingray-api/services';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { Account, Customer, Person, Organization } from '@app/core/stingray-api/models';
import { finalize } from 'rxjs/operators';
import { SelectionModel } from '@angular/cdk/collections';
import { ToasterService } from '@app/core/toaster/toaster.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { StingrayAuthenticationService } from '@app/core/authentication/stingray-authentication.service';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { CustomerAutocompleteComponent } from '../customer-autocomplete/customer-autocomplete.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';


@Component({
    selector: 'unite-accounts-show',
    templateUrl: './accounts-show.component.html',
    styleUrls: ['./accounts-show.component.scss'],
    imports: [MatDialogTitle, TranslateModule, MatDialogContent, MatProgressSpinner, LoadingButtonComponent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatCheckbox, CustomerAutocompleteComponent, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatSortHeader, MatIcon, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatDialogActions, MatButton, MatDialogClose]
})
export class AccountsShowComponent implements OnInit {
  isLoadingAccount = false;
  isSavingAccount = false;
  isSendingVerificationEmail = false;
  isSendingPasswordResetEmail = false;

  hasAccount = false;

  account: Account;
  formGroup: UntypedFormGroup;
  customerGroup: UntypedFormGroup;
  organization: Organization;

  customersDataSource: MatTableDataSource<Customer> = new MatTableDataSource();
  customersDisplayedColumns = ['select', 'name'];
  customersSelection = new SelectionModel<Customer>(true, []);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private accountsService: AccountsService,
    private personsService: PersonsService,
    private toasterService: ToasterService,
    private translateService: TranslateService,
    private stingrayAuthenticationService: StingrayAuthenticationService,
    @Inject(MAT_DIALOG_DATA) public person: Person,
    private matDialogRef: MatDialogRef<AccountsShowComponent>) {


    this.organization = this.stingrayAuthenticationService.organization;

    this.formGroup = this.formBuilder.group({
      auth0UserName: [, [Validators.required, Validators.email]],
      blocked: []
    });

    this.customerGroup = this.formBuilder.group({
      customer: []
    });

    this.customerGroup.get('customer').valueChanges.subscribe((customer: Customer) => {

      /* If the observable doesn't return a customer (is the case when just typing) */
      if (!customer.id) {
        return;
      }

      this.addCustomerToSelectionList(customer);
    });
  }

  ngOnInit() {
    this.isLoadingAccount = true;
    this.accountsService.getAccountByPersonId({ id: this.person.id })
      .pipe(finalize(() => this.isLoadingAccount = false))
      .subscribe(account => {

        this.hasAccount = !!account;
        this.account = account;

        /* Preload the form with data for the account, or initialize with defaults from the person */
        if (this.account) {
          this.formGroup.patchValue(this.account, { emitEvent: false });
        } else {
          this.formGroup.patchValue({ auth0UserName: this.person.email ? this.person.email.toLowerCase() : '' }, { emitEvent: false });
        }

        this.isLoadingAccount = true;
        this.personsService.getPersonRelatedCustomers({ id: this.person.id })
          .pipe(finalize(() => this.isLoadingAccount = false))
          .subscribe(relatedCustomers => {
            console.log('Person related customers', relatedCustomers);

            /* If we have to create the account we preselect all customers */
            if (!this.hasAccount) {
              this.customersDataSource.data = relatedCustomers;
              this.masterToggle();
            } else {

              const customers = [...relatedCustomers];

              const accountCustomerIds = this.account.customers.map(t => t.id);
              const relatedCustomerIds = relatedCustomers.map(t => t.id);

              /* Add accounts that are not in the related customers results */
              this.account.customers.filter(c => relatedCustomerIds.indexOf(c.id) === -1).every(c => customers.push(c));

              /* Bind customers to the table data source */
              this.customersDataSource.data = customers;

              /* Preselect customers this account already has access to */
              const selectedCustomers = this.customersDataSource.data.filter(c => accountCustomerIds.indexOf(c.id) > -1);
              this.customersSelection = new SelectionModel<Customer>(true, selectedCustomers);
            }
          });
      });
  }

  isAllSelected() {
    const numSelected = this.customersSelection.selected.length;
    const numRows = this.customersDataSource.filteredData.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.customersSelection.clear() :
      this.customersDataSource.filteredData.forEach(row => this.customersSelection.select(row));
  }

  save() {
    this.isSavingAccount = true;
    const account = this.ConvertFormToAccount();

    this.accountsService.updateAccount({ id: this.account.id, body: account })
      .pipe(finalize(() => this.isSavingAccount = false))
      .subscribe(changedAccount => {
        this.toasterService.success(this.translateService.instant('Account updated'));
        this.account = changedAccount;
        this.matDialogRef.close(changedAccount);
      });
  }

  add() {
    this.isSavingAccount = true;
    const account = this.ConvertFormToAccount();

    this.accountsService.addAccount({ body: account })
      .pipe(finalize(() => this.isSavingAccount = false))
      .subscribe((a) => {
        let account = a;
        this.toasterService.success(this.translateService.instant('Account created'));
        this.matDialogRef.close(a);
      });
  }

  sendPasswordResetEmail() {
    this.isSendingPasswordResetEmail = true;
    this.accountsService.sendAccountPasswordResetEmail({ id: this.account.id })
      .pipe(finalize(() => this.isSendingPasswordResetEmail = false))
      .subscribe(() => {
        this.toasterService.success(this.translateService.instant('Password reset mail sent'));
        this.matDialogRef.close();
      });
  }

  sendVerificationEmail() {
    this.isSendingVerificationEmail = true;
    this.accountsService.resendVerificationEmail({ id: this.account.id })
      .pipe(finalize(() => this.isSendingVerificationEmail = false))
      .subscribe(() => {
        this.toasterService.success(this.translateService.instant('Verification email sent'));
        this.matDialogRef.close();
      });
  }

  /**
   * Converts the form data to a backend model
   */
  private ConvertFormToAccount() {
    const account = <Account>{
      auth0UserName: this.formGroup.get('auth0UserName').value,
      blocked: !!this.formGroup.get('blocked').value,
      customers: this.customersSelection.selected,
      person: this.person
    };
    return account;
  }

  /**
   * Adds a customer to the customer selection list
   * @param customer the customer to add to the list
   */
  private addCustomerToSelectionList(customer: Customer) {
    /* Check if the customer is already in the list */
    const alreadyIsInList = this.customersDataSource.data.filter(t => t.id === customer.id).length > 0;

    if (alreadyIsInList) {
      this.toasterService.warning(this.translateService.instant('Customer is already in the list'));
      return;
    }

    /* Add the customer to the list of customers */
    const customers = this.customersDataSource.data;
    customers.unshift(customer);
    this.customersDataSource.data = customers;

    /* Add the customer to the selected list of customers */
    const selectedCustomers = this.customersSelection.selected;
    selectedCustomers.push(customer);

    this.customersSelection = new SelectionModel<Customer>(true, selectedCustomers);
  }
}
